import * as THREE from 'three';

/**
 * Procedural soldier: mesh, rig and animation.
 *
 * There are no animation assets in this project, so every pose in the game is
 * *computed*. The rig is a 17-bone hierarchy (pelvis -> spine -> chest -> neck ->
 * head, plus three-bone arms and legs) driven by a layered animation stack:
 *
 *   locomotion  -> aim overlay -> weapon pose -> recoil/flinch springs -> ragdoll
 *
 * Two design choices carry most of the visual quality:
 *
 * 1. **Stride length is derived from speed, and pelvis height is derived from the
 *    legs.** The hip swing amplitude is solved from `asin(stride / 4L)` so the
 *    planted foot travels backwards at (approximately) the speed the body travels
 *    forwards — that is what kills the ice-skating look. The pelvis is then placed
 *    so the lower of the two ankles sits exactly on the ground plane, which
 *    produces the vertical bob and the double-support dip for free, and guarantees
 *    feet never sink.
 *
 * 2. **One skinned mesh per material, geometry shared by every enemy.** All eight
 *    soldiers reference the same BufferGeometry and differ only in their Skeleton,
 *    so a squad costs three draw calls each and no per-spawn geometry work.
 *
 * Rendering-space conventions: model faces -Z, up is +Y, the character's right is
 * +X. A bone's `rotation.x` is positive when it pitches its -Z axis upward (so a
 * positive thigh angle swings the leg forward, and a positive head angle looks up).
 */

/* ------------------------------------------------------------------ *
 * Tuning                                                              *
 * ------------------------------------------------------------------ */

export const TUNING = {
  height: 1.8,
  thighLen: 0.45,
  shinLen: 0.43,
  ankleRest: 0.07,

  // Locomotion. Feet are placed in world space and the legs are solved to reach
  // them, so a planted foot cannot slide: the step length sets the cycle time
  // (2 * stepLen / speed), which at 5 m/s lands the run cycle on ~0.55 s.
  stepBase: 0.42,
  stepPerSpeed: 0.19,
  stepMax: 1.4,
  dutyWalk: 0.56, // fraction of the cycle a foot is on the ground...
  dutyRun: 0.32, // ...running has a flight phase, walking has double support
  maxLegFrac: 0.985, // never fully lock the knee
  hipHalfWidth: 0.105,
  footHalfWidth: 0.085, // feet track narrower than the hips, as people walk

  // Arm IK. Grips are points on the rifle, in the weapon's local space, that the
  // wrists are solved onto — the firing hand at the pistol grip, the support
  // hand on the handguard.
  upperArmLen: 0.27,
  forearmLen: 0.25,
  gripRight: [0.005, 0.025, 0.055],
  gripLeft: [-0.005, 0.075, -0.235],
  runAt: 4.4, // speed where the run blend reaches 1
  walkAt: 1.1,
  footLiftWalk: 0.055,
  footLiftRun: 0.17,

  armSwingWalk: 0.28,
  armSwingRun: 0.92,
  torsoLeanRun: 0.26,
  pelvisYaw: 0.15,
  chestCounter: 0.62,
  pelvisSway: 0.038,
  pelvisRoll: 0.075,

  // Aim overlay: how the yaw/pitch is distributed up the spine, and human limits.
  aimYawShare: { spine: 0.20, chest: 0.38, neck: 0.24, head: 0.18 },
  aimPitchShare: { spine: 0.14, chest: 0.34, neck: 0.26, head: 0.26 },
  maxAimYaw: 1.15,
  maxAimPitch: 0.95,

  crouchDrop: 0.42,

  // Springs
  recoilFreq: 26,
  recoilDamp: 0.55,
  flinchFreq: 17,
  flinchDamp: 0.42,

  // Ragdoll
  ragdollIterations: 4,
  ragdollGravity: 17.5,
  ragdollDamp: 0.988,
  ragdollFriction: 0.62,
  ragdollSettle: 0.06,
};

/* ------------------------------------------------------------------ *
 * Rig definition                                                      *
 * ------------------------------------------------------------------ */

const B = TUNING;

/** name, parent, local bind offset. Bind rotations are all identity. */
const BONE_DEFS = [
  ['pelvis', null, [0, 0.98, 0]],
  ['spine', 'pelvis', [0, 0.11, 0]],
  ['chest', 'spine', [0, 0.19, 0]],
  ['neck', 'chest', [0, 0.22, 0]],
  ['head', 'neck', [0, 0.09, 0]],

  ['upperArmL', 'chest', [-0.195, 0.16, 0]],
  ['forearmL', 'upperArmL', [0, -0.27, 0]],
  ['handL', 'forearmL', [0, -0.25, 0]],
  ['upperArmR', 'chest', [0.195, 0.16, 0]],
  ['forearmR', 'upperArmR', [0, -0.27, 0]],
  ['handR', 'forearmR', [0, -0.25, 0]],

  ['thighL', 'pelvis', [-0.105, -0.03, 0]],
  ['shinL', 'thighL', [0, -B.thighLen, 0]],
  ['footL', 'shinL', [0, -B.shinLen, 0]],
  ['thighR', 'pelvis', [0.105, -0.03, 0]],
  ['shinR', 'thighR', [0, -B.thighLen, 0]],
  ['footR', 'shinR', [0, -B.shinLen, 0]],
];

const BONE_INDEX = new Map(BONE_DEFS.map((d, i) => [d[0], i]));

/** Bind-pose world position of every bone (root at the origin, no rotations). */
const BIND_POS = (() => {
  const out = new Map();
  for (const [name, parent, p] of BONE_DEFS) {
    const base = parent ? out.get(parent) : new THREE.Vector3();
    out.set(name, new THREE.Vector3(p[0], p[1], p[2]).add(base));
  }
  return out;
})();

const bp = (n) => BIND_POS.get(n);

/* ------------------------------------------------------------------ *
 * Geometry construction                                               *
 * ------------------------------------------------------------------ */

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3();

/** Place a geometry in bind space. `rot` is XYZ euler, `scale` a per-axis vector. */
function place(geo, pos, rot = null, scale = null) {
  _e.set(rot ? rot[0] : 0, rot ? rot[1] : 0, rot ? rot[2] : 0);
  _q.setFromEuler(_e);
  _s.set(scale ? scale[0] : 1, scale ? scale[1] : 1, scale ? scale[2] : 1);
  _m.compose(new THREE.Vector3(pos[0], pos[1], pos[2]), _q, _s);
  geo.applyMatrix4(_m);
  return geo;
}

const box = (w, h, d, pos, rot, scale) => place(new THREE.BoxGeometry(w, h, d), pos, rot, scale);
const sph = (r, pos, scale, seg = 10) =>
  place(new THREE.SphereGeometry(r, seg, Math.max(6, seg - 2)), pos, null, scale);
const cyl = (rt, rb, h, pos, rot, scale, seg = 10) =>
  place(new THREE.CylinderGeometry(rt, rb, h, seg, 1), pos, rot, scale);
const cap = (r, len, pos, rot, scale, seg = 9) =>
  place(new THREE.CapsuleGeometry(r, len, 3, seg), pos, rot, scale);

/**
 * Merge a set of {geo, bone, color} parts into one skinned BufferGeometry.
 * Written by hand rather than pulling in BufferGeometryUtils: we need skin
 * indices and per-part vertex colours anyway, and this keeps the module
 * dependency-free.
 */
function mergeSkinned(parts) {
  let vCount = 0;
  let iCount = 0;
  for (const p of parts) {
    vCount += p.geo.attributes.position.count;
    iCount += p.geo.index ? p.geo.index.count : p.geo.attributes.position.count;
  }

  const pos = new Float32Array(vCount * 3);
  const nor = new Float32Array(vCount * 3);
  const uv = new Float32Array(vCount * 2);
  const col = new Float32Array(vCount * 3);
  const si = new Uint16Array(vCount * 4);
  const sw = new Float32Array(vCount * 4);
  const idx = vCount > 65535 ? new Uint32Array(iCount) : new Uint16Array(iCount);

  let vo = 0;
  let io = 0;
  for (const p of parts) {
    const g = p.geo;
    const n = g.attributes.position.count;
    pos.set(g.attributes.position.array, vo * 3);
    nor.set(g.attributes.normal.array, vo * 3);
    if (g.attributes.uv) uv.set(g.attributes.uv.array, vo * 2);
    const bone = BONE_INDEX.get(p.bone);
    const c = p.color;
    for (let i = 0; i < n; i++) {
      col[(vo + i) * 3] = c[0];
      col[(vo + i) * 3 + 1] = c[1];
      col[(vo + i) * 3 + 2] = c[2];
      si[(vo + i) * 4] = bone;
      sw[(vo + i) * 4] = 1;
    }
    if (g.index) {
      const a = g.index.array;
      for (let i = 0; i < a.length; i++) idx[io + i] = a[i] + vo;
      io += a.length;
    } else {
      for (let i = 0; i < n; i++) idx[io + i] = vo + i;
      io += n;
    }
    vo += n;
    g.dispose();
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
  geo.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  // Generous manual bounds: animation and ragdolls push well past the bind pose.
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.9, 0), 1.7);
  return geo;
}

function mergePlain(parts) {
  let vCount = 0;
  let iCount = 0;
  for (const p of parts) {
    vCount += p.geo.attributes.position.count;
    iCount += p.geo.index ? p.geo.index.count : p.geo.attributes.position.count;
  }
  const pos = new Float32Array(vCount * 3);
  const nor = new Float32Array(vCount * 3);
  const uv = new Float32Array(vCount * 2);
  const col = new Float32Array(vCount * 3);
  const idx = vCount > 65535 ? new Uint32Array(iCount) : new Uint16Array(iCount);
  let vo = 0;
  let io = 0;
  for (const p of parts) {
    const g = p.geo;
    const n = g.attributes.position.count;
    pos.set(g.attributes.position.array, vo * 3);
    nor.set(g.attributes.normal.array, vo * 3);
    if (g.attributes.uv) uv.set(g.attributes.uv.array, vo * 2);
    for (let i = 0; i < n; i++) {
      col[(vo + i) * 3] = p.color[0];
      col[(vo + i) * 3 + 1] = p.color[1];
      col[(vo + i) * 3 + 2] = p.color[2];
    }
    if (g.index) {
      const a = g.index.array;
      for (let i = 0; i < a.length; i++) idx[io + i] = a[i] + vo;
      io += a.length;
    } else {
      for (let i = 0; i < n; i++) idx[io + i] = vo + i;
      io += n;
    }
    vo += n;
    g.dispose();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Vertex-colour gains, multiplied against the forge's albedo.
 *
 * This palette is a *gameplay* requirement before it is an art one. The map is
 * dry dirt (~#c4a486), pale concrete and red brick — all light, all warm. A
 * soldier painted in the same value band as that ground is invisible at 20 m no
 * matter how good the animation is, and the player cannot engage what he cannot
 * find. So the figure is built as a deliberate three-step value ladder:
 *
 *   near-black gear (0.10–0.16)  <  cool olive uniform (0.30)  <  dirt (~0.65)
 *
 * The uniform sits at roughly *half* the ground's value and is pushed green/cool
 * against warm dirt and red brick, so it separates by hue as well as by value.
 * Helmet, plate carrier, webbing, gloves, knee pads and boots are all darker
 * than the cloth — that ordering is what makes a human read as an armed one, and
 * it must never invert (a helmet brighter than the face reads as a bald mannequin).
 * A few square centimetres of light tape stop him becoming a featureless
 * silhouette when he stands against asphalt or deep shadow.
 */
const C = {
  cloth: [0.40, 0.44, 0.34], // olive drab uniform, the lightest large area
  clothLeg: [0.365, 0.40, 0.315], // trousers a shade below the blouse
  face: [0.26, 0.255, 0.245], // balaclava — lighter than the helmet above it
  neck: [0.105, 0.11, 0.105], // shadowed neck: separates head from shoulders
  glove: [0.11, 0.11, 0.115],
  helmet: [0.155, 0.16, 0.155],
  carrier: [0.115, 0.12, 0.12],
  webbing: [0.10, 0.10, 0.105], // straps, knee pads, sling
  pouch: [0.185, 0.16, 0.13], // dark coyote — still below the uniform
  boot: [0.105, 0.105, 0.11],
  sole: [0.145, 0.145, 0.15],
  tape: [0.55, 0.52, 0.44], // cat-eyes and arm patch: tiny, light, load-bearing
  visor: [0.09, 0.11, 0.145], // smoked lens, faintly blue
  gunMetal: [0.115, 0.115, 0.125],
  gunPoly: [0.13, 0.13, 0.125],
  gunAccent: [0.42, 0.44, 0.48], // worn edges and the optic lens only
};

/**
 * Uniform multiplier per team. Index is the TEAM value.
 *
 * Chosen to hold luminance roughly constant — 0.30·1.14 + 0.59·0.93 + 0.11·0.82 is
 * within 2% of 1.0 — so the value ladder in `C` survives a team swap intact. The
 * hostile side leans warm, which is the harder read against warm dirt; that is
 * deliberate, because it is the side the player is *hunting* rather than the side
 * they need to spot instantly to avoid shooting.
 */
const TEAM_TINT = [
  [0.93, 1.0, 1.06], // 0 · ALPHA — the player's side, a touch cooler and bluer
  [1.14, 0.93, 0.82], // 1 · BRAVO — hostiles, rust
];

let _cachedGeo = null;

/** Builds (once) the shared soldier geometry: cloth, gear and rifle. */
function buildGeometry() {
  if (_cachedGeo) return _cachedGeo;

  const cloth = [];
  const gear = [];
  const add = (list, geo, bone, color) => list.push({ geo, bone, color });

  /* ---------------- torso ---------------- */
  const pel = bp('pelvis');
  const spi = bp('spine');
  const che = bp('chest');
  const nec = bp('neck');
  const hea = bp('head');

  // Pelvis / hips
  add(cloth, cyl(0.155, 0.145, 0.19, [0, pel.y - 0.03, 0], null, [1, 1, 0.72]), 'pelvis', C.cloth);
  add(gear, cyl(0.162, 0.158, 0.055, [0, pel.y + 0.05, 0], null, [1, 1, 0.74]), 'pelvis', C.pouch); // belt
  // Drop-leg holster, right thigh
  add(gear, box(0.055, 0.14, 0.09, [0.16, pel.y - 0.16, 0.01], [0.1, 0, 0.05]), 'thighR', C.pouch);
  // Thigh dump pouch, left
  add(gear, box(0.06, 0.11, 0.085, [-0.155, pel.y - 0.12, 0.02], [0, 0, -0.06]), 'thighL', C.pouch);

  // Abdomen
  add(cloth, cyl(0.158, 0.15, 0.2, [0, spi.y + 0.02, 0], null, [1, 1, 0.7]), 'spine', C.cloth);
  // Chest
  add(cloth, cyl(0.185, 0.166, 0.30, [0, che.y + 0.11, 0], null, [1, 1, 0.68]), 'chest', C.cloth);
  // Trapezius wedge. Deliberately low and flat: the old one was a 0.10 m sphere
  // centred on the neck bone and it swallowed the whole neck, which is why the
  // head used to sit straight on the shoulders like a snowman's.
  add(cloth, sph(0.186, [0, che.y + 0.195, 0], [1.02, 0.44, 0.70]), 'chest', C.cloth);

  // Plate carrier: front + back plates plus a cummerbund ring.
  add(gear, box(0.30, 0.32, 0.055, [0, che.y + 0.10, -0.10], [0.03, 0, 0]), 'chest', C.carrier);
  add(gear, box(0.29, 0.30, 0.05, [0, che.y + 0.10, 0.105], [-0.02, 0, 0]), 'chest', C.carrier);
  add(gear, cyl(0.196, 0.19, 0.13, [0, che.y + 0.02, 0], null, [1, 1, 0.70]), 'chest', C.pouch);
  // Mag pouches, front
  for (let i = -1; i <= 1; i++) {
    add(gear, box(0.078, 0.115, 0.06, [i * 0.085, che.y + 0.03, -0.145], [0.04, 0, 0]), 'chest', C.pouch);
  }
  // Admin pouch / radio, upper chest and back
  add(gear, box(0.10, 0.07, 0.045, [-0.085, che.y + 0.19, -0.135], null), 'chest', C.pouch);
  add(gear, box(0.085, 0.13, 0.06, [0.075, che.y + 0.16, 0.135], null), 'chest', C.pouch);
  add(gear, cyl(0.006, 0.006, 0.30, [0.11, che.y + 0.32, 0.14], [0.18, 0, 0.1]), 'chest', C.gunAccent); // antenna
  // Shoulder straps
  for (const sx of [-1, 1]) {
    add(gear, box(0.075, 0.055, 0.26, [sx * 0.105, che.y + 0.245, 0], [0, 0, sx * 0.1]), 'chest', C.carrier);
  }

  // Assault pack. A shouldered rifle is invisible from directly behind, so the
  // back of the figure has to carry its own "this is a soldier" cue — a pack,
  // its compression straps and the sling are readable from 30 m and from any
  // angle, where the weapon is not.
  add(gear, box(0.285, 0.33, 0.135, [0, che.y + 0.115, 0.185], [-0.03, 0, 0]), 'chest', C.carrier);
  add(gear, box(0.20, 0.10, 0.055, [0, che.y + 0.045, 0.255], null), 'chest', C.pouch);
  for (const sy of [0.185, 0.045]) {
    add(gear, box(0.30, 0.022, 0.05, [0, che.y + sy, 0.255], null), 'chest', C.webbing);
  }
  add(gear, cyl(0.042, 0.042, 0.17, [-0.115, che.y + 0.09, 0.24], null, [1, 1, 1]), 'chest', C.pouch); // canteen
  // Sling: over the left shoulder, across the back and the chest to the right
  // hip. Two straps, near-black, and they cut the torso in half diagonally.
  add(gear, box(0.036, 0.34, 0.032, [0.03, che.y + 0.10, -0.135], [0, 0, -0.55]), 'chest', C.webbing);
  add(gear, box(0.036, 0.32, 0.032, [-0.02, che.y + 0.12, 0.142], [0, 0, 0.5]), 'chest', C.webbing);

  /* ---------------- head ---------------- */
  // Neck. Long and dark enough that ~5 cm of it shows between the collar and
  // the helmet lip: that shadow gap is what makes the head read as a head.
  add(cloth, cyl(0.059, 0.067, 0.155, [0, nec.y + 0.045, 0.004], null, [1, 1, 0.92]), 'neck', C.neck);
  // Balaclava'd skull, a little larger than before — the head was reading as
  // about a ninth of the body where a helmeted soldier's is closer to a seventh.
  add(cloth, sph(0.105, [0, hea.y + 0.056, 0.004], [1, 1.14, 1.06], 12), 'head', C.face);
  add(cloth, box(0.118, 0.082, 0.098, [0, hea.y + 0.002, -0.046], [0.12, 0, 0]), 'head', C.face); // jaw
  // Helmet: shell + band + brow. The brow is the single most legible feature on
  // a soldier's silhouette at range, and it is what was missing.
  add(gear, sph(0.132, [0, hea.y + 0.078, 0.006], [1, 0.94, 1.03], 14), 'head', C.helmet);
  add(gear, cyl(0.133, 0.138, 0.082, [0, hea.y + 0.050, 0.006], null, [1, 1, 1.03]), 'head', C.helmet);
  add(gear, box(0.215, 0.028, 0.085, [0, hea.y + 0.070, -0.108], [0.30, 0, 0]), 'head', C.helmet); // brow
  add(gear, box(0.19, 0.03, 0.06, [0, hea.y + 0.062, 0.108], [-0.25, 0, 0]), 'head', C.helmet); // rear lip
  add(gear, box(0.115, 0.03, 0.055, [0, hea.y + 0.122, -0.105], [0.35, 0, 0]), 'head', C.webbing); // NVG mount
  // Cat-eyes: two centimetres of light tape on the back of the shell. Tiny, but
  // they stop the head vanishing when he stands against asphalt or in shadow.
  for (const sx of [-1, 1]) {
    add(gear, box(0.030, 0.018, 0.012, [sx * 0.045, hea.y + 0.088, 0.126], [0, 0, 0]), 'head', C.tape);
    add(gear, box(0.016, 0.048, 0.155, [sx * 0.125, hea.y + 0.058, 0.012], [0, 0, sx * -0.06]), 'head', C.webbing);
    add(gear, cyl(0.040, 0.040, 0.032, [sx * 0.119, hea.y + 0.0, 0.012], [0, 0, Math.PI / 2]), 'head', C.webbing);
  }
  // Goggles / eye protection, tucked under the brow
  add(gear, box(0.195, 0.05, 0.045, [0, hea.y + 0.044, -0.090], [0.06, 0, 0]), 'head', C.visor);

  /* ---------------- arms ---------------- */
  for (const side of ['L', 'R']) {
    const sx = side === 'L' ? -1 : 1;
    const ua = bp('upperArm' + side);
    const fa = bp('forearm' + side);
    const ha = bp('hand' + side);
    add(gear, sph(0.080, [ua.x, ua.y, 0], [1, 0.95, 1]), 'upperArm' + side, C.carrier); // shoulder pad
    add(cloth, cap(0.055, 0.16, [ua.x, ua.y - 0.145, 0]), 'upperArm' + side, C.cloth);
    add(gear, box(0.058, 0.058, 0.062, [ua.x + sx * 0.044, ua.y - 0.09, 0]), 'upperArm' + side, C.webbing); // brassard
    add(gear, box(0.040, 0.030, 0.030, [ua.x + sx * 0.062, ua.y - 0.09, -0.012]), 'upperArm' + side, C.tape); // patch
    add(cloth, cap(0.048, 0.15, [fa.x, fa.y - 0.13, 0]), 'forearm' + side, C.cloth);
    add(gear, sph(0.058, [fa.x, fa.y, 0]), 'forearm' + side, C.webbing); // elbow pad
    add(gear, box(0.052, 0.052, 0.036, [fa.x, fa.y - 0.215, -0.02]), 'forearm' + side, C.webbing); // cuff
    add(gear, box(0.074, 0.10, 0.064, [ha.x, ha.y - 0.048, -0.004]), 'hand' + side, C.glove);
    add(gear, box(0.03, 0.055, 0.05, [ha.x + sx * 0.035, ha.y - 0.098, -0.012], [0, 0, sx * -0.3]), 'hand' + side, C.glove);
  }

  /* ---------------- legs ---------------- *
   * The old leg was two capsules of the same colour from hip to ankle, which is
   * why it read as a pale khaki tube with texture banding on it. A leg reads as
   * a leg from three breaks: a knee that is proud of the limb and much darker
   * than it, a calf that tapers into a narrow ankle, and a boot that flares back
   * out under a bloused trouser cuff. All three are rebuilt here.                */
  for (const side of ['L', 'R']) {
    const th = bp('thigh' + side);
    const sh = bp('shin' + side);
    const ft = bp('foot' + side);
    const sx = side === 'L' ? -1 : 1;

    add(gear, sph(0.100, [th.x, th.y, 0], [1, 0.9, 1]), 'thigh' + side, C.clothLeg);
    add(cloth, cap(0.089, 0.26, [th.x, th.y - 0.205, 0]), 'thigh' + side, C.clothLeg);
    // Outboard cargo pocket: kills the smooth tube and still reads at 30 m.
    add(gear, box(0.042, 0.135, 0.12, [th.x + sx * 0.073, th.y - 0.20, -0.008], [0, 0, sx * 0.04]),
      'thigh' + side, C.pouch);

    // Knee pad + retention strap, proud of the limb and near-black.
    add(gear, sph(0.088, [sh.x, sh.y + 0.004, -0.024], [1.02, 1.26, 1.06]), 'shin' + side, C.webbing);
    add(gear, cyl(0.083, 0.083, 0.026, [sh.x, sh.y - 0.058, 0], null, [1, 1, 0.96]), 'shin' + side, C.webbing);

    // Calf: tapered, with a gastrocnemius bulge, down to a narrow ankle.
    add(cloth, cyl(0.077, 0.055, 0.27, [sh.x, sh.y - 0.155, 0]), 'shin' + side, C.clothLeg);
    add(cloth, sph(0.070, [sh.x, sh.y - 0.115, 0.030], [0.95, 1.30, 0.85]), 'shin' + side, C.clothLeg);
    // Bloused trouser cuff, flaring down over the boot top.
    add(cloth, cyl(0.070, 0.090, 0.078, [sh.x, ft.y + 0.218, 0]), 'shin' + side, C.clothLeg);

    // Boot: a tall near-black shaft, a pinched ankle, then the foot and sole.
    // A fifth of the leg's height in the darkest value on the model — that is
    // the "boot break" that anchors the figure to the ground.
    add(gear, cyl(0.084, 0.077, 0.19, [ft.x, ft.y + 0.130, 0.004]), 'shin' + side, C.boot);
    add(gear, cyl(0.070, 0.070, 0.055, [ft.x, ft.y + 0.030, 0.004]), 'foot' + side, C.boot);
    add(gear, box(0.098, 0.085, 0.245, [ft.x, ft.y - 0.020, -0.048], [0.03, 0, 0]), 'foot' + side, C.boot);
    add(gear, sph(0.052, [ft.x, ft.y - 0.022, -0.155], [1, 0.78, 1.1]), 'foot' + side, C.boot);
    add(gear, box(0.106, 0.030, 0.256, [ft.x, ft.y - 0.060, -0.049]), 'foot' + side, C.sole);
    add(gear, box(0.100, 0.028, 0.082, [ft.x, ft.y - 0.050, 0.040]), 'foot' + side, C.sole); // heel block
  }

  /* ---------------- rifle (unskinned, local space, forward -Z) ----------------
   * Grown from 0.78 m to 0.92 m overall. The old carbine was short enough to sit
   * entirely inside the torso's outline when shouldered, so a soldier facing away
   * looked unarmed. Now the muzzle clears the body in front and the stock and
   * optic clear the shoulder line behind, which is what actually communicates
   * "armed" from the rear — combined with the sling and the pack on the back.   */
  const rifle = [];
  const radd = (geo, color) => rifle.push({ geo, color });
  radd(box(0.054, 0.082, 0.32, [0, 0.055, -0.02]), C.gunPoly); // receiver
  radd(box(0.048, 0.022, 0.42, [0, 0.101, -0.08]), C.gunMetal); // top rail
  radd(cyl(0.0125, 0.0125, 0.40, [0, 0.048, -0.36], [Math.PI / 2, 0, 0]), C.gunMetal); // barrel
  radd(box(0.058, 0.068, 0.30, [0, 0.05, -0.30]), C.gunPoly); // handguard
  for (let i = 0; i < 6; i++) radd(box(0.062, 0.009, 0.015, [0, 0.018, -0.19 - i * 0.045]), C.gunMetal);
  radd(cyl(0.022, 0.019, 0.08, [0, 0.048, -0.565], [Math.PI / 2, 0, 0]), C.gunMetal); // flash hider
  radd(cyl(0.021, 0.021, 0.20, [0, 0.062, 0.20], [Math.PI / 2, 0, 0]), C.gunPoly); // buffer tube
  radd(box(0.052, 0.128, 0.055, [0, 0.050, 0.305]), C.gunPoly); // butt pad
  radd(box(0.056, 0.058, 0.14, [0, 0.022, 0.215], [0.16, 0, 0]), C.gunPoly); // cheek riser
  radd(box(0.038, 0.12, 0.055, [0, -0.045, 0.05], [-0.3, 0, 0]), C.gunPoly); // grip
  radd(box(0.038, 0.20, 0.082, [0, -0.088, -0.075], [0.13, 0, 0]), C.gunPoly); // magazine
  radd(box(0.044, 0.058, 0.115, [0, 0.145, -0.055]), C.gunMetal); // optic body
  radd(cyl(0.026, 0.026, 0.09, [0, 0.160, -0.055], [Math.PI / 2, 0, 0]), C.gunMetal);
  radd(cyl(0.022, 0.022, 0.006, [0, 0.160, -0.101], [Math.PI / 2, 0, 0]), C.gunAccent); // lens
  radd(box(0.032, 0.032, 0.075, [0.038, 0.05, -0.37]), C.gunPoly); // laser box
  radd(box(0.030, 0.10, 0.046, [0, -0.018, -0.335], [0.10, 0, 0]), C.gunPoly); // vertical foregrip

  _cachedGeo = {
    cloth: mergeSkinned(cloth),
    gear: mergeSkinned(gear),
    rifle: mergePlain(rifle),
    muzzleLocal: new THREE.Vector3(0, 0.048, -0.605),
    sightLocal: new THREE.Vector3(0, 0.160, -0.1),
  };
  return _cachedGeo;
}

/** Cloned, vertex-coloured materials. One set per forge, shared by all enemies. */
function getMaterials(ctx) {
  const forge = ctx.forge;
  if (forge.__enemyMats) return forge.__enemyMats;
  const mk = (id, repeat) => {
    const m = forge.tiled(id, repeat).clone();
    m.vertexColors = true;
    m.userData = { materialId: 'flesh' };
    return m;
  };
  // A limb capsule wraps one whole UV tile along its length, so at repeat 1 the
  // weave's *low* frequencies land on the leg as half-a-dozen fat horizontal
  // rings — the trousers read as corrugated pipe. Tiling several times over the
  // same limb pushes the pattern below the size of a feature, which is where
  // cloth is supposed to live: visible as material, invisible as stripes.
  const mats = {
    cloth: mk('fabric', 3.2),
    gear: mk('polymer', 2.4),
    rifle: mk('gunmetal', 1.4),
    flash: new THREE.MeshBasicMaterial({
      color: 0xffd9a0,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  };
  mats.gear.metalness = 0.12;
  mats.cloth.metalness = 0.0;
  // Soften the weave relief further. A limb capsule wraps one whole UV around a
  // ~0.5 m circumference, so the weave's low frequencies land on the leg as
  // horizontal banding — which is exactly what made the trousers read as ribbed
  // tubes. At this scale the relief is finer than a pixel anyway; keeping it
  // near-flat costs nothing and removes the banding.
  mats.cloth.normalScale.set(0.28, 0.28);
  mats.gear.normalScale.set(0.55, 0.55);
  mats.cloth.roughness = 1.0;
  forge.__enemyMats = mats;
  return mats;
}

/* ------------------------------------------------------------------ *
 * Poses                                                               *
 * ------------------------------------------------------------------ */

/**
 * Only the *weapon* is authored now: where the rifle sits relative to the chest
 * in each carry state. The arms are solved onto it (see `_solveArm`), so the
 * hands stay on the grips through every blend, recoil kick and aim twist.
 *
 * The rifle hangs off the chest rather than off a hand, which is what guarantees
 * the muzzle points exactly where the AI is aiming.
 */
const POSE = {
  // rifle slung across the back, hands loose at the sides
  relaxed: {
    L: { u: [0.02, 0.06, 0.16], f: [0.34, -0.1, 0], h: [0, 0, 0] },
    R: { u: [0.02, -0.06, -0.16], f: [0.34, 0.1, 0], h: [0, 0, 0] },
    anchor: { p: [-0.09, 0.06, 0.15], r: [0.15, -0.45, 1.35] },
  },
  // patrol carry, muzzle down and across the body
  lowReady: { anchor: { p: [0.135, 0.045, -0.17], r: [-0.5, -0.06, 0.05] } },
  // Shouldered: the stock sits in the shoulder pocket, below the eye, and the
  // head comes down to the sight rather than the rifle coming up to the head.
  // Held 4 cm further outboard than it used to be — directly under the shoulder
  // joint (x = 0.195), which is both where a real stock goes and far enough out
  // that the barrel and optic break the torso's outline instead of hiding in it.
  aim: { anchor: { p: [0.185, 0.147, -0.10], r: [0.02, -0.03, 0.02] } },
};

/* ------------------------------------------------------------------ *
 * Model                                                               *
 * ------------------------------------------------------------------ */

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _qc = new THREE.Quaternion();
const _up = new THREE.Vector3(0, 1, 0);
const _dn = new THREE.Vector3(0, -1, 0);

/* Leg-solver scratch. Kept separate from the shared temporaries so the IK can
   never be clobbered by the ragdoll or muzzle helpers. */
const _lv1 = new THREE.Vector3();
const _lv2 = new THREE.Vector3();
const _lq = new THREE.Quaternion();
const _lqi = new THREE.Quaternion();
const _le = new THREE.Euler();
const _aq = new THREE.Quaternion();
const _ae = new THREE.Euler();
const _av = new THREE.Vector3();
const _ikN = new THREE.Vector3();
const _ikAx = new THREE.Vector3();
const _ikU = new THREE.Vector3();
const _ikY = new THREE.Vector3();
const _ikZ = new THREE.Vector3();
const _ikM = new THREE.Matrix4();
const _aq2 = new THREE.Quaternion();
const _relaxQ = new THREE.Quaternion();
const _relaxE = new THREE.Euler();

/* The middle joint is displaced *opposite* the pole, so these point away from
   where the knee and elbow should end up: knees lead forward, elbows hang back,
   down and slightly outboard. */
const KNEE_POLE = new THREE.Vector3(0, 0, 1);
const ELBOW_POLE = {
  L: new THREE.Vector3(0.42, 0.5, -0.76).normalize(),
  R: new THREE.Vector3(-0.42, 0.5, -0.76).normalize(),
};

/**
 * Exact two-bone IK for a chain whose bones point down their local -Y and whose
 * middle joint bends about its local X.
 *
 * The naive approach — decompose the shoulder->wrist direction into Euler angles
 * and then add the half-angle offset to `rotation.x` — is subtly wrong: the
 * offset rotates about the *parent's* X while the joint actually bends about the
 * *bone's own* X. Those coincide only when the limb hangs straight, so the error
 * is invisible on a walking leg and enormous on an arm reaching across the body.
 *
 * Building the parent's orientation as an explicit basis puts the bend axis
 * exactly where the child expects it, so the end effector lands on the target.
 *
 * `pole` is the direction the middle joint should point (knee forward, elbow back).
 * Writes the parent's quaternion into `outQ` and returns the joint angle.
 */
function twoBoneIK(sx, sy, sz, tx, ty, tz, L1, L2, pole, outQ) {
  _ikN.set(tx - sx, ty - sy, tz - sz);
  let d = _ikN.length();
  const maxLen = (L1 + L2) * 0.995;
  if (d > maxLen) d = maxLen;
  if (d < 0.03) d = 0.03;
  _ikN.normalize();

  const bend = Math.acos(clamp((d * d - L1 * L1 - L2 * L2) / (2 * L1 * L2), -1, 1));
  const alpha = Math.atan2(L2 * Math.sin(bend), L1 + L2 * Math.cos(bend));

  _ikAx.crossVectors(_ikN, pole);
  if (_ikAx.lengthSq() < 1e-8) _ikAx.set(1, 0, 0);
  else _ikAx.normalize();

  // Upper bone: the target direction swung back by the half-angle, in the plane
  // the joint actually bends in.
  _ikU.copy(_ikN).applyAxisAngle(_ikAx, -alpha);

  _ikY.copy(_ikU).negate(); // local +Y is up the bone
  _ikZ.crossVectors(_ikAx, _ikY);
  _ikM.makeBasis(_ikAx, _ikY, _ikZ);
  outQ.setFromRotationMatrix(_ikM);
  return bend;
}
const _legScratch = [
  { side: 'L', phi: 0, fx: 0, fy: 0, fz: 0, hox: 0, hoy: 0, hoz: 0 },
  { side: 'R', phi: 0, fx: 0, fy: 0, fz: 0, hox: 0, hoy: 0, hoz: 0 },
];

/** Gaussian bump on a wrapped [0,1) phase. */
function bump(p, c, w) {
  let d = p - c;
  d -= Math.round(d);
  const t = d / w;
  return Math.exp(-t * t * 2.4);
}

const clamp = THREE.MathUtils.clamp;
const smooth = (a, b, x) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};
function wrapAngle(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export class EnemyModel {
  /**
   * @param {number} team 0 = the player's side, 1 = hostile. Only the uniform hue
   *   changes with it — see `TEAM_TINT`.
   */
  constructor(ctx, variant = 0, team = 1) {
    this.ctx = ctx;
    const geo = buildGeometry();
    const mats = getMaterials(ctx);
    this.geo = geo;

    this.root = new THREE.Group();
    this.root.matrixAutoUpdate = true;

    /* --- bones --- */
    this.bones = [];
    this.bone = {};
    for (const [name, parent, p] of BONE_DEFS) {
      const b = new THREE.Bone();
      b.name = name;
      b.position.set(p[0], p[1], p[2]);
      this.bones.push(b);
      this.bone[name] = b;
      if (parent) this.bone[parent].add(b);
      else this.root.add(b);
    }

    const inverses = BONE_DEFS.map(([name]) => {
      const w = bp(name);
      return new THREE.Matrix4().makeTranslation(-w.x, -w.y, -w.z);
    });
    this.skeleton = new THREE.Skeleton(this.bones, inverses);

    // Per-variant tint so a squad doesn't look like eight clones. Kept narrow
    // and biased cool: the whole point of the uniform's value is that it does
    // not drift back towards the warm dirt it has to be seen against.
    const tint = [
      [1, 1, 1],
      [0.90, 0.97, 1.03],
      [1.04, 1.0, 0.92],
    ][variant % 3];
    /**
     * Team hue, multiplied on top of the variant tint.
     *
     * It has to be a *hue* shift and not a brightness one. The comment on `C` above
     * spends a paragraph on why the uniform sits at roughly half the ground's value,
     * and a team colour that lightened one side would undo exactly that — an ally
     * you cannot pick out of dry dirt is worse than an ally you cannot tell from an
     * enemy. Both teams therefore keep the same value ladder and separate by hue:
     * the player's side stays the authored cool olive, hostiles go warm and rusty.
     *
     * Hue alone is not enough on its own — it fails in shadow, at range, and for a
     * colour-blind player — so this is the *second* signal. The first is the
     * overhead marker every ally carries (see `Teamplay.js`).
     */
    const teamTint = TEAM_TINT[team] ?? TEAM_TINT[1];
    const rgb = [tint[0] * teamTint[0], tint[1] * teamTint[1], tint[2] * teamTint[2]];
    const neutral = rgb[0] === 1 && rgb[1] === 1 && rgb[2] === 1;
    const clothMat = neutral ? mats.cloth : mats.cloth.clone();
    if (!neutral) clothMat.color.setRGB(rgb[0], rgb[1], rgb[2]);

    this.meshCloth = new THREE.SkinnedMesh(geo.cloth, clothMat);
    this.meshGear = new THREE.SkinnedMesh(geo.gear, mats.gear);
    for (const m of [this.meshCloth, this.meshGear]) {
      m.castShadow = true;
      m.receiveShadow = true;
      m.frustumCulled = true;
      this.root.add(m);
      // Bind with an identity bind matrix: geometry is authored in bind space, so
      // the mesh's own world transform must not be double-counted when it moves.
      m.bind(this.skeleton, new THREE.Matrix4());
    }

    /* --- weapon --- */
    this.weaponAnchor = new THREE.Object3D();
    this.bone.chest.add(this.weaponAnchor);
    this.rifle = new THREE.Mesh(geo.rifle, mats.rifle);
    this.rifle.castShadow = true;
    this.weaponAnchor.add(this.rifle);

    this.muzzle = new THREE.Object3D();
    this.muzzle.position.copy(geo.muzzleLocal);
    this.rifle.add(this.muzzle);

    // A stretched additive blob reads as a flash from any angle and lets the
    // bloom pass do the work — a billboard quad would go edge-on from the side.
    this.flash = new THREE.Mesh(new THREE.SphereGeometry(0.075, 8, 6), mats.flash);
    this.flash.visible = false;
    this.flash.position.set(0, 0, -0.07);
    this.muzzle.add(this.flash);
    this._flashT = 0;

    /* --- animation state --- */
    this.phase = ctx.rng ? ctx.rng() : Math.random();
    this.yaw = 0;
    this.speed = 0;
    this.runBlend = 0;
    this.crouch = 0;
    this.holdWeight = 0; // 0 = slung on back, 1 = in hands
    this.aimWeight = 0; // 0 = low ready, 1 = shouldered
    this.aimYaw = 0;
    this.aimPitch = 0;
    this.footEvent = 0; // 0 none, 1 left, 2 right
    this._lastPhase = 0;
    this.dead = false;

    this._recoil = { v: 0, x: 0, kv: 0, k: 0 };
    this._flinch = { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, limb: null, lv: 0, l: 0 };
    this._breath = ctx.rng ? ctx.rng() * 10 : Math.random() * 10;
    this._idleTimer = 0;
    this._smoothAimYaw = 0;
    this._smoothAimPitch = 0;
    this._pelvisY = 0.98;

    this.ragdoll = null;
  }

  /* ---------------------------------------------------------------- */

  get muzzleWorld() {
    this.muzzle.getWorldPosition(_v1);
    return _v1;
  }

  /** World-space muzzle position written into `out`. */
  getMuzzle(out) {
    return this.muzzle.getWorldPosition(out);
  }

  fireFlash() {
    this._flashT = 0.05;
    this.flash.visible = true;
    this.flash.rotation.z = Math.random() * Math.PI;
    const s = 0.8 + Math.random() * 0.5;
    this.flash.scale.set(s, s, s * 2.6);
    // Recoil impulse into the spring.
    this._recoil.v += 9.5;
    this._recoil.kv += 7.0;
  }

  /** Directional flinch. `dirLocal` points along the bullet travel, in world space. */
  flinch(worldDir, tag, strength = 1) {
    const f = this._flinch;
    _v1.copy(worldDir).normalize();
    // Convert to model space (yaw only).
    const c = Math.cos(-this.yaw);
    const s = Math.sin(-this.yaw);
    const lx = _v1.x * c + _v1.z * s;
    const lz = -_v1.x * s + _v1.z * c;
    const k = strength * 9;
    f.vx += -lz * k * 0.5; // shot from the front -> torso pitches back
    f.vz += -lx * k * 0.6;
    f.vy += lx * k * 0.35;
    f.limb = tag;
    f.lv += strength * 12;
  }

  /* ---------------------------------------------------------------- *
   * Main update                                                       *
   * ---------------------------------------------------------------- */

  update(dt, s) {
    if (this.ragdoll) {
      this._updateRagdoll(dt);
      this.root.updateMatrixWorld(true);
      return;
    }
    if (!s) return;

    this.footEvent = 0;

    // --- flash decay ---
    if (this._flashT > 0) {
      this._flashT -= dt;
      if (this._flashT <= 0) this.flash.visible = false;
    }

    const speed = s.speed ?? 0;
    this.speed = speed;
    this.yaw = s.yaw ?? this.yaw;
    this.crouch = THREE.MathUtils.damp(this.crouch, s.crouch ?? 0, 9, dt);
    this.holdWeight = THREE.MathUtils.damp(this.holdWeight, s.hold ?? 0, 6, dt);
    this.aimWeight = THREE.MathUtils.damp(this.aimWeight, s.aim ?? 0, 8, dt);

    this.root.position.copy(s.position);
    this.root.rotation.set(0, this.yaw, 0);

    // --- phase ---
    const runBlend = smooth(TUNING.walkAt, TUNING.runAt, speed);
    this.runBlend = runBlend;
    const moving = speed > 0.28;

    // Direction of travel in model space decides whether the legs cycle forwards
    // or backwards; a pure sidestep still cycles, just slower.
    let fwdSpeed = speed;
    let latSpeed = 0;
    if (s.moveLocal) {
      fwdSpeed = -s.moveLocal.z;
      latSpeed = s.moveLocal.x;
    }
    const cycleSpeed = Math.hypot(fwdSpeed, latSpeed);
    this.strafe = THREE.MathUtils.damp(this.strafe ?? 0, clamp(latSpeed / 3, -1, 1), 8, dt);

    // Unit travel direction in model space. Steps are laid down along it, so a
    // soldier who sidesteps out of cover actually sidesteps instead of scissoring
    // his legs fore-and-aft while sliding.
    if (cycleSpeed > 0.05) {
      this._mvx = latSpeed / cycleSpeed;
      this._mvz = -fwdSpeed / cycleSpeed;
    } else {
      this._mvx = 0;
      this._mvz = -1;
    }

    // Step length is the one authored number; everything else follows from it.
    // Cycle time is 2 * stepLen / speed, so the foot placed on the ground travels
    // backwards at exactly the speed the body travels forwards.
    this.stepLen = clamp(TUNING.stepBase + cycleSpeed * TUNING.stepPerSpeed, 0.3, TUNING.stepMax);
    this.duty = THREE.MathUtils.lerp(TUNING.dutyWalk, TUNING.dutyRun, runBlend);

    if (moving) {
      // Phase always advances forwards; direction is carried by the step vector,
      // so backpedalling plants the foot behind and rolls it forwards, correctly.
      this.phase += (cycleSpeed / (2 * this.stepLen)) * dt;
      this.phase -= Math.floor(this.phase);
    } else {
      // Ease the legs back to neutral rather than freezing mid-stride.
      const target = this.phase < 0.25 || this.phase > 0.75 ? 0 : 0.5;
      this.phase += wrapAngle((target - this.phase) * Math.PI * 2) / (Math.PI * 2) * Math.min(1, dt * 9);
      if (this.phase < 0) this.phase += 1;
      if (this.phase >= 1) this.phase -= 1;
    }

    // --- footfall events ---
    const pf = this.phase;
    if (moving) {
      const crossed = (a, b, x) => (a <= b ? x > a && x <= b : x > a || x <= b);
      if (crossed(this._lastPhase, pf, 0.0)) this.footEvent = 1;
      else if (crossed(this._lastPhase, pf, 0.5)) this.footEvent = 2;
    }
    this._lastPhase = pf;

    this._breath += dt;

    this._animateLegs(dt, moving, runBlend);
    this._animateTorso(dt, s, runBlend, moving);
    this._animateArms(dt, runBlend, moving);
    this._springs(dt);

    this.root.updateMatrixWorld(true);
  }

  /* ------------------------------- legs ------------------------------ */

  /**
   * Legs are solved, not posed.
   *
   * Each foot gets a *target position in root space*. During stance the target
   * slides backwards at exactly the rate the root moves forwards, so the foot is
   * pinned in the world; during swing it arcs forward with a clearance hump. The
   * pelvis is then placed as high as both legs can still reach their targets,
   * which produces the vertical bob, the double-support dip and the running
   * bounce as consequences of geometry rather than as hand-tuned curves. Finally
   * a closed-form two-bone IK recovers hip and knee angles.
   *
   * Forward kinematics — picking hip and knee curves and hoping the foot lands
   * where the body is going — is what produces ice-skating. This cannot: the
   * planted foot's world position is the input, not the output.
   */
  _animateLegs(dt, moving, runBlend) {
    const bnd = this.bone;
    const L = TUNING.thighLen;
    const S = TUNING.shinLen;
    const maxLeg = (L + S) * TUNING.maxLegFrac;
    const crouch = this.crouch;
    const rest = TUNING.ankleRest;
    const duty = this.duty;
    const half = this.stepLen * duty; // half the stance excursion
    const lift = TUNING.footLiftWalk + runBlend * TUNING.footLiftRun;
    const strafe = this.strafe ?? 0;
    const mv = moving ? 1 : 0;

    // Pelvis planar motion has to be known before the IK, because it moves the
    // hip sockets the feet are being solved against.
    const two = this.phase * Math.PI * 2;
    const sinP = Math.sin(two);
    const cosP = Math.cos(two);
    const sway = -TUNING.pelvisSway * sinP * mv * (0.5 + runBlend * 0.9);
    this._pelSway = sway;
    this._pelYaw = -TUNING.pelvisYaw * cosP * mv * (0.45 + runBlend);
    this._pelRoll = TUNING.pelvisRoll * sinP * mv * (0.5 + runBlend * 0.7);
    _lq.setFromEuler(_le.set(0, this._pelYaw, this._pelRoll));
    _lqi.copy(_lq).invert();

    /* ---- foot targets in root space ---- */
    const legs = _legScratch;
    for (let i = 0; i < 2; i++) {
      const l = legs[i];
      l.side = i === 0 ? 'L' : 'R';
      const sgn = i === 0 ? -1 : 1; // left is -X
      l.phi = i === 0 ? this.phase : (this.phase + 0.5) % 1;
      const baseX = sgn * TUNING.footHalfWidth + strafe * 0.07 * sgn;
      if (!moving) {
        l.fx = baseX;
        l.fz = i === 0 ? 0.015 : -0.03; // a natural, slightly staggered stand
        l.fy = rest;
      } else {
        // `u` runs +half (ahead along travel) to -half (behind) across stance,
        // and back again across swing. Laying it along the travel vector is what
        // keeps a sidestep from turning into a forward-facing shuffle.
        let u;
        if (l.phi < duty) {
          u = half - 2 * half * (l.phi / duty);
          l.fy = rest;
        } else {
          const t = (l.phi - duty) / (1 - duty);
          const te = t * t * (3 - 2 * t); // ease off the toe, decelerate into the heel
          u = -half + 2 * half * te;
          l.fy = rest + lift * Math.pow(Math.sin(Math.PI * t), 0.75);
        }
        l.fx = baseX + this._mvx * u;
        l.fz = this._mvz * u;
      }
      // Hip socket offset, rotated by the pelvis.
      _lv1.set(sgn * TUNING.hipHalfWidth, -0.03, 0).applyQuaternion(_lq);
      l.hox = _lv1.x;
      l.hoy = _lv1.y;
      l.hoz = _lv1.z;
    }

    /* ---- pelvis height: as high as both legs can still reach ---- */
    let pelvisY = Infinity;
    for (const l of legs) {
      const dx = l.fx - (sway + l.hox);
      const dz = l.fz - l.hoz;
      const v = Math.sqrt(Math.max(0.0025, maxLeg * maxLeg - (dx * dx + dz * dz)));
      const limit = l.fy - l.hoy + v;
      if (limit < pelvisY) pelvisY = limit;
    }
    // A touch of impact absorption on each footfall, on top of the geometry.
    const impact = moving ? bump(this.phase, 0.03, 0.09) + bump(this.phase, 0.53, 0.09) : 0;
    this._pelvisY = pelvisY - impact * (0.006 + runBlend * 0.022) - crouch * TUNING.crouchDrop;

    /* ---- two-bone IK ---- */
    for (const l of legs) {
      const hipY = this._pelvisY + l.hoy;
      _lv2.set(l.fx - (sway + l.hox), l.fy - hipY, l.fz - l.hoz).applyQuaternion(_lqi);
      let d = _lv2.length();
      if (d > maxLeg) {
        _lv2.multiplyScalar(maxLeg / d);
        d = maxLeg;
      } else if (d < 0.15) {
        _lv2.set(0, -0.15, 0);
        d = 0.15;
      }
      // Law of cosines for the knee, then tilt the thigh off the hip->ankle line
      // by the half-angle so the ankle lands exactly on target.
      const th = bnd['thigh' + l.side];
      const q = twoBoneIK(
        th.position.x, th.position.y, th.position.z,
        _lv2.x + th.position.x, _lv2.y + th.position.y, _lv2.z + th.position.z,
        L, S, KNEE_POLE, _aq2
      );
      th.quaternion.copy(_aq2);
      th.rotation.setFromQuaternion(_aq2);
      bnd['shin' + l.side].rotation.set(q, 0, 0);

      // Sole level with the ground, plus heel strike and toe-off roll.
      const roll = moving
        ? 0.26 * bump(l.phi, 0.015, 0.075) -
          (0.34 + runBlend * 0.36) * bump(l.phi, duty, 0.09) +
          0.22 * bump(l.phi, duty + (1 - duty) * 0.45, 0.16)
        : 0;
      bnd['foot' + l.side].rotation.set(clamp(-(th.rotation.x + q) + roll, -0.95, 0.62), 0, 0);
    }
  }

  /* ------------------------------ torso ------------------------------ */

  _animateTorso(dt, s, runBlend, moving) {
    const bnd = this.bone;
    const two = this.phase * Math.PI * 2;
    const cos = Math.cos(two);
    const sin = Math.sin(two);
    const crouch = this.crouch;
    const moveAmt = moving ? 1 : 0;

    // Pelvis sway / transverse rotation / frontal roll were solved with the legs,
    // which had to know where the hip sockets would end up.
    const sway = this._pelSway;
    const pelYaw = this._pelYaw;
    const pelRoll = this._pelRoll;

    const pelvis = bnd.pelvis;
    pelvis.position.set(sway, this._pelvisY, 0);
    pelvis.rotation.set(0, pelYaw, pelRoll);
    void sin;
    void cos;

    // Torso lean grows with speed; crouching adds a forward hunch.
    const lean = TUNING.torsoLeanRun * runBlend * moveAmt + crouch * 0.30;
    const breath = Math.sin(this._breath * 1.7) * 0.012 * (1 - runBlend);

    // Aim overlay. Yaw/pitch relative to the body's facing, clamped to human limits.
    let ay = 0;
    let ap = 0;
    if (s.aimDir) {
      const d = s.aimDir;
      const want = Math.atan2(-d.x, -d.z);
      ay = clamp(wrapAngle(want - this.yaw), -TUNING.maxAimYaw, TUNING.maxAimYaw);
      ap = clamp(Math.asin(clamp(d.y, -1, 1)), -TUNING.maxAimPitch, TUNING.maxAimPitch);
    }
    // Damp so the upper body swings rather than snapping.
    this._smoothAimYaw = THREE.MathUtils.damp(this._smoothAimYaw, ay, 11, dt);
    this._smoothAimPitch = THREE.MathUtils.damp(this._smoothAimPitch, ap, 11, dt);
    ay = this._smoothAimYaw;
    ap = this._smoothAimPitch;
    this.aimYaw = ay;
    this.aimPitch = ap;

    const yS = TUNING.aimYawShare;
    const pS = TUNING.aimPitchShare;
    const counter = -TUNING.chestCounter * TUNING.pelvisYaw * -cos * moveAmt * (0.45 + runBlend);
    const f = this._flinch;

    bnd.spine.rotation.set(
      -lean * 0.45 + ap * pS.spine + breath + f.x * 0.35,
      ay * yS.spine + counter * 0.4,
      f.z * 0.3 + pelRoll * -0.3
    );
    bnd.chest.rotation.set(
      -lean * 0.5 + ap * pS.chest - this._recoil.x * 0.35 + f.x * 0.5,
      ay * yS.chest + counter * 0.6 + f.y * 0.5,
      f.z * 0.5 + pelRoll * -0.35 + this._recoil.x * 0.1
    );
    bnd.neck.rotation.set(ap * pS.neck + f.x * 0.3, ay * yS.neck, f.z * 0.2);
    // Head stabilises against torso lean so the eyes stay on target.
    bnd.head.rotation.set(
      ap * pS.head + lean * 0.55 - this._recoil.x * 0.25 + f.x * 0.35,
      ay * yS.head + f.y * 0.4,
      f.z * 0.35 + Math.sin(this._breath * 2.3) * 0.01
    );
  }

  /* ------------------------------- arms ------------------------------ */

  /**
   * Two-bone IK for one arm, solved in chest space (the upper arm's parent).
   * `tx,ty,tz` is the wrist target. The elbow bends forwards, which is the only
   * direction a human elbow goes.
   */
  /** Solve one arm onto a wrist target in chest space. Result lands in `_aq2`. */
  _solveArm(side, tx, ty, tz) {
    const sh = this.bone['upperArm' + side].position;
    return twoBoneIK(
      sh.x, sh.y, sh.z, tx, ty, tz,
      TUNING.upperArmLen, TUNING.forearmLen, ELBOW_POLE[side], _aq2
    );
  }

  _animateArms(dt, runBlend, moving) {
    const bnd = this.bone;
    const hold = this.holdWeight;

    /* ---- weapon anchor: slung -> low ready -> shouldered ---- */
    const A = POSE.relaxed.anchor;
    const Bp = POSE.lowReady.anchor;
    const Cp = POSE.aim.anchor;
    const aw = this.aimWeight;
    const mix = (u, v, w, i) => {
      const held = v[i] + (w[i] - v[i]) * aw;
      return u[i] + (held - u[i]) * hold;
    };
    const bounce = moving ? Math.sin(this.phase * Math.PI * 4) * 0.012 * (0.4 + runBlend) * (1 - aw * 0.7) : 0;
    const anchor = this.weaponAnchor;
    anchor.position.set(
      mix(A.p, Bp.p, Cp.p, 0),
      mix(A.p, Bp.p, Cp.p, 1) + bounce,
      mix(A.p, Bp.p, Cp.p, 2) + this._recoil.k * 0.05
    );
    anchor.rotation.set(
      mix(A.r, Bp.r, Cp.r, 0) - this._recoil.k * 0.22,
      mix(A.r, Bp.r, Cp.r, 1),
      mix(A.r, Bp.r, Cp.r, 2)
    );

    /* ---- hands: solved onto the weapon, never authored ---- */
    // Hand-written arm angles cannot survive the weapon moving between three
    // carry poses plus recoil — the hands drift off the rifle and the illusion
    // dies. Solving both arms at the grips means they are always on it.
    _aq.setFromEuler(_ae.set(anchor.rotation.x, anchor.rotation.y, anchor.rotation.z));

    for (const side of ['L', 'R']) {
      const sgn = side === 'L' ? -1 : 1;
      const a = POSE.relaxed[side];
      const swingAmp =
        (TUNING.armSwingWalk + (TUNING.armSwingRun - TUNING.armSwingWalk) * runBlend) * (moving ? 1 : 0);
      const phi = side === 'L' ? this.phase : (this.phase + 0.5) % 1;
      const swing = -swingAmp * Math.cos(phi * Math.PI * 2);
      const elbowSwing =
        (0.30 + runBlend * 0.95) * (moving ? 1 : 0) * (0.55 + 0.45 * Math.cos(phi * Math.PI * 2));
      const rec = this._recoil.k * (side === 'R' ? 1 : 0.72) * hold;

      const uarm = bnd['upperArm' + side];
      const farm = bnd['forearm' + side];
      const hand = bnd['hand' + side];

      // Relaxed / slung pose, as a quaternion so it can be slerped against IK.
      _relaxE.set(a.u[0] + swing, a.u[1], a.u[2] + sgn * 0.02 + sgn * this.crouch * 0.05);
      _relaxQ.setFromEuler(_relaxE);

      let bend = a.f[0] + elbowSwing;
      if (hold > 0.002) {
        const g = side === 'R' ? TUNING.gripRight : TUNING.gripLeft;
        _av.set(g[0], g[1], g[2]).applyQuaternion(_aq).add(anchor.position);
        const solved = this._solveArm(side, _av.x, _av.y, _av.z);
        uarm.quaternion.slerpQuaternions(_relaxQ, _aq2, hold);
        bend = bend * (1 - hold) + solved * hold;
      } else {
        uarm.quaternion.copy(_relaxQ);
      }
      uarm.rotation.setFromQuaternion(uarm.quaternion);

      farm.rotation.set(bend - rec * 0.3, a.f[1] * (1 - hold), a.f[2] * (1 - hold));
      hand.rotation.set(
        a.h[0] * (1 - hold) + (side === 'R' ? 0.1 : 0.16) * hold,
        0,
        a.h[2] * (1 - hold) + sgn * 0.26 * hold
      );
    }
  }

  /* ------------------------------ springs ---------------------------- */

  _springs(dt) {
    const step = (val, vel, freq, damp) => {
      const w = freq;
      const a = -w * w * val - 2 * damp * w * vel;
      return [val + vel * dt + 0.5 * a * dt * dt, vel + a * dt];
    };
    let r = step(this._recoil.x, this._recoil.v, TUNING.recoilFreq, TUNING.recoilDamp);
    this._recoil.x = r[0];
    this._recoil.v = r[1];
    r = step(this._recoil.k, this._recoil.kv, TUNING.recoilFreq * 0.8, 0.6);
    this._recoil.k = r[0];
    this._recoil.kv = r[1];

    const f = this._flinch;
    const fx = step(f.x, f.vx, TUNING.flinchFreq, TUNING.flinchDamp);
    f.x = fx[0];
    f.vx = fx[1];
    const fy = step(f.y, f.vy, TUNING.flinchFreq, TUNING.flinchDamp);
    f.y = fy[0];
    f.vy = fy[1];
    const fz = step(f.z, f.vz, TUNING.flinchFreq, TUNING.flinchDamp);
    f.z = fz[0];
    f.vz = fz[1];
    const fl = step(f.l, f.lv, 22, 0.4);
    f.l = fl[0];
    f.lv = fl[1];

    // Limb-local flinch on the struck part, on top of the pose.
    if (f.limb && Math.abs(f.l) > 0.002) {
      const b = this.bone[f.limb];
      if (b) b.rotation.x += f.l * 0.5;
    }
  }

  /* ---------------------------------------------------------------- *
   * Ragdoll                                                           *
   * ---------------------------------------------------------------- */

  /** Node table: particle name -> bone it samples, plus a local offset. */
  static NODES = [
    ['pelvis', 'pelvis', [0, 0, 0], 0.55],
    ['spineMid', 'spine', [0, 0.06, 0], 0.7],
    ['chest', 'chest', [0, 0.12, 0], 0.6],
    ['neck', 'neck', [0, 0.04, 0], 1.0],
    ['head', 'head', [0, 0.09, 0], 1.1],
    ['shL', 'upperArmL', [0, 0, 0], 1.0],
    ['elL', 'forearmL', [0, 0, 0], 1.5],
    ['wrL', 'handL', [0, -0.07, 0], 1.9],
    ['shR', 'upperArmR', [0, 0, 0], 1.0],
    ['elR', 'forearmR', [0, 0, 0], 1.5],
    ['wrR', 'handR', [0, -0.07, 0], 1.9],
    ['hipL', 'thighL', [0, 0, 0], 0.8],
    ['knL', 'shinL', [0, 0, 0], 1.2],
    ['anL', 'footL', [0, -0.03, 0], 1.5],
    ['hipR', 'thighR', [0, 0, 0], 0.8],
    ['knR', 'shinR', [0, 0, 0], 1.2],
    ['anR', 'footR', [0, -0.03, 0], 1.5],
  ];

  static LINKS = [
    // skeleton chain
    ['pelvis', 'spineMid', 1],
    ['spineMid', 'chest', 1],
    ['chest', 'neck', 1],
    ['neck', 'head', 1],
    ['chest', 'shL', 1],
    ['chest', 'shR', 1],
    ['shL', 'elL', 1],
    ['elL', 'wrL', 1],
    ['shR', 'elR', 1],
    ['elR', 'wrR', 1],
    ['pelvis', 'hipL', 1],
    ['pelvis', 'hipR', 1],
    ['hipL', 'knL', 1],
    ['knL', 'anL', 1],
    ['hipR', 'knR', 1],
    ['knR', 'anR', 1],
    // stiffeners keep the torso a body instead of a chain
    ['shL', 'shR', 0.9],
    ['hipL', 'hipR', 0.9],
    ['pelvis', 'chest', 0.85],
    ['spineMid', 'neck', 0.6],
    ['chest', 'hipL', 0.7],
    ['chest', 'hipR', 0.7],
    ['shL', 'hipL', 0.5],
    ['shR', 'hipR', 0.5],
    ['neck', 'shL', 0.6],
    ['neck', 'shR', 0.6],
  ];

  /** Minimum separations that stop joints hyperextending backwards. */
  static SPREAD = [
    ['hipL', 'anL', 0.30],
    ['hipR', 'anR', 0.30],
    ['shL', 'wrL', 0.22],
    ['shR', 'wrR', 0.22],
    ['pelvis', 'head', 0.45],
    ['chest', 'knL', 0.35],
    ['chest', 'knR', 0.35],
  ];

  /**
   * Convert the current animated pose into a verlet particle system and let
   * physics take over. The killing blow's direction is injected as an impulse on
   * the struck region, so a headshot snaps the head back and a leg shot drops them.
   */
  startRagdoll(impulseDir, strength = 1, tag = 'chest', velocity = null, feetY = null) {
    if (this.ragdoll) return;
    this.dead = true;
    this.root.updateMatrixWorld(true);
    const baseGround = feetY ?? this.root.position.y;

    const nodes = [];
    const byName = new Map();
    for (const [name, boneName, off, invMass] of EnemyModel.NODES) {
      const b = this.bone[boneName];
      const p = new THREE.Vector3(off[0], off[1], off[2]).applyMatrix4(b.matrixWorld);
      const n = {
        name,
        p,
        prev: p.clone(),
        invMass,
        groundY: baseGround,
        groundAge: 99,
      };
      if (velocity) n.prev.addScaledVector(velocity, -1 / 60);
      nodes.push(n);
      byName.set(name, n);
    }

    const links = [];
    for (const [a, b, stiff] of EnemyModel.LINKS) {
      const na = byName.get(a);
      const nb = byName.get(b);
      links.push({ a: na, b: nb, len: na.p.distanceTo(nb.p), stiff });
    }
    const spread = [];
    for (const [a, b, min] of EnemyModel.SPREAD) {
      spread.push({ a: byName.get(a), b: byName.get(b), min });
    }

    // Impulse: push the struck region along the bullet, add a little spin.
    const dir = _v1.copy(impulseDir || _dn).normalize();
    const region = {
      head: ['head', 'neck', 'chest'],
      chest: ['chest', 'neck', 'shL', 'shR'],
      stomach: ['spineMid', 'pelvis', 'chest'],
      arms: ['shL', 'shR', 'elL', 'elR'],
      legs: ['hipL', 'hipR', 'knL', 'knR'],
    }[tag] || ['chest'];
    const kick = 0.006 * strength;
    for (const n of nodes) {
      const w = region.includes(n.name) ? 1 : 0.28;
      n.prev.addScaledVector(dir, -kick * w * (1 + n.invMass * 0.4));
      n.prev.y += 0.0006 * (Math.random() - 0.5);
    }

    this.ragdoll = { nodes, byName, links, spread, time: 0, settled: false, sleep: 0 };

    // Freeze the visual weapon in place: drop it from the chest and let it fall
    // with the body by re-parenting to the pelvis bone (cheap, no extra sim).
    this.weaponAnchor.position.set(0.09, 0.02, -0.02);
    this.weaponAnchor.rotation.set(0.4, -0.2, 0.9);
    this.flash.visible = false;
  }

  _updateRagdoll(dt) {
    const rd = this.ragdoll;
    if (rd.settled) return;
    rd.time += dt;

    const physics = this.ctx.physics;
    const h = Math.min(dt, 1 / 45);
    const g = TUNING.ragdollGravity * h * h;
    const damp = TUNING.ragdollDamp;

    let maxMove = 0;
    for (const n of rd.nodes) {
      const px = n.p.x;
      const py = n.p.y;
      const pz = n.p.z;
      let vx = (n.p.x - n.prev.x) * damp;
      let vy = (n.p.y - n.prev.y) * damp;
      let vz = (n.p.z - n.prev.z) * damp;

      // Ground query is a spatial-hash lookup; refresh it a few times a second.
      n.groundAge += dt;
      if (n.groundAge > 0.12) {
        n.groundAge = 0;
        const gy = physics.groundHeight(n.p.x, n.p.z, n.p.y + 0.4, 6);
        // A miss returns fromY - maxDist; keep the last good value instead of
        // letting the corpse fall out of the world over a hole in the collider set.
        if (gy > n.p.y - 5.5) n.groundY = gy;
      }

      n.p.x += vx;
      n.p.y += vy - g;
      n.p.z += vz;
      n.prev.set(px, py, pz);

      const floor = n.groundY + 0.055;
      if (n.p.y < floor) {
        n.p.y = floor;
        // Tangential friction so bodies stop sliding instead of gliding to a halt.
        const fr = TUNING.ragdollFriction;
        n.prev.x = n.p.x - (n.p.x - n.prev.x) * (1 - fr);
        n.prev.z = n.p.z - (n.p.z - n.prev.z) * (1 - fr);
        if (n.prev.y > n.p.y) n.prev.y = n.p.y;
      }
      maxMove = Math.max(maxMove, Math.abs(vx) + Math.abs(vy) + Math.abs(vz));
    }

    for (let it = 0; it < TUNING.ragdollIterations; it++) {
      for (const l of rd.links) {
        const a = l.a;
        const b = l.b;
        const dx = b.p.x - a.p.x;
        const dy = b.p.y - a.p.y;
        const dz = b.p.z - a.p.z;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
        const diff = ((d - l.len) / d) * 0.5 * l.stiff;
        const wa = a.invMass / (a.invMass + b.invMass);
        const wb = 1 - wa;
        a.p.x += dx * diff * 2 * wa;
        a.p.y += dy * diff * 2 * wa;
        a.p.z += dz * diff * 2 * wa;
        b.p.x -= dx * diff * 2 * wb;
        b.p.y -= dy * diff * 2 * wb;
        b.p.z -= dz * diff * 2 * wb;
      }
      for (const sp of rd.spread) {
        const a = sp.a;
        const b = sp.b;
        const dx = b.p.x - a.p.x;
        const dy = b.p.y - a.p.y;
        const dz = b.p.z - a.p.z;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
        if (d >= sp.min) continue;
        const diff = ((d - sp.min) / d) * 0.25;
        a.p.x += dx * diff;
        a.p.y += dy * diff;
        a.p.z += dz * diff;
        b.p.x -= dx * diff;
        b.p.y -= dy * diff;
        b.p.z -= dz * diff;
      }
      // Floor re-projection after constraints so nothing is pulled underground.
      for (const n of rd.nodes) {
        const floor = n.groundY + 0.055;
        if (n.p.y < floor) n.p.y = floor;
      }
    }

    // Torso capsule vs world so bodies don't end up inside walls.
    const pv = rd.byName.get('pelvis');
    const sweepDelta = _v2.set(pv.p.x - pv.prev.x, 0, pv.p.z - pv.prev.z);
    if (sweepDelta.lengthSq() > 1e-8) {
      const from = _v1.set(pv.prev.x, Math.max(pv.groundY, pv.p.y - 0.2), pv.prev.z);
      const res = physics.sweepCapsule(from, 0.22, 0.35, sweepDelta);
      if (res.hitWall) {
        const dx = res.position.x - pv.p.x;
        const dz = res.position.z - pv.p.z;
        for (const n of rd.nodes) {
          n.p.x += dx;
          n.p.z += dz;
          n.prev.x += dx;
          n.prev.z += dz;
        }
      }
    }

    // Sleep once motion dies down: corpses cost nothing after ~2 s.
    if (maxMove < TUNING.ragdollSettle * h) rd.sleep += dt;
    else rd.sleep = 0;
    if (rd.sleep > 0.5 || rd.time > 8) rd.settled = true;

    this._applyRagdollToBones();
  }

  _applyRagdollToBones() {
    const rd = this.ragdoll;
    const n = (name) => rd.byName.get(name).p;

    const pelvis = n('pelvis');
    this.root.position.set(pelvis.x, pelvis.y - 0.98, pelvis.z);
    this.root.rotation.set(0, this.yaw, 0);
    this.root.updateMatrix();
    this.root.quaternion.setFromEuler(this.root.rotation);

    // Walk the chain parent-first, tracking each bone's world quaternion so we can
    // convert a desired world direction into a local rotation exactly.
    const worldQ = new Map();
    worldQ.set(null, _qc.copy(this.root.quaternion).clone());

    const orient = (boneName, from, to, bindDir, parentName) => {
      const b = this.bone[boneName];
      _v1.subVectors(n(to), n(from));
      if (_v1.lengthSq() < 1e-8) return;
      _v1.normalize();
      _qa.setFromUnitVectors(bindDir, _v1); // world rotation of this bone
      const pq = worldQ.get(parentName) || worldQ.get(null);
      _qb.copy(pq).invert().multiply(_qa);
      b.quaternion.copy(_qb);
      b.rotation.setFromQuaternion(_qb);
      worldQ.set(boneName, _qa.clone());
    };

    this.bone.pelvis.position.set(0, 0.98, 0);
    orient('pelvis', 'pelvis', 'spineMid', _up, null);
    orient('spine', 'spineMid', 'chest', _up, 'pelvis');
    orient('chest', 'chest', 'neck', _up, 'spine');
    orient('neck', 'neck', 'head', _up, 'chest');
    this.bone.head.quaternion.slerp(_qa.identity(), 0.25);

    orient('upperArmL', 'shL', 'elL', _dn, 'chest');
    orient('forearmL', 'elL', 'wrL', _dn, 'upperArmL');
    orient('upperArmR', 'shR', 'elR', _dn, 'chest');
    orient('forearmR', 'elR', 'wrR', _dn, 'upperArmR');
    orient('thighL', 'hipL', 'knL', _dn, 'pelvis');
    orient('shinL', 'knL', 'anL', _dn, 'thighL');
    orient('thighR', 'hipR', 'knR', _dn, 'pelvis');
    orient('shinR', 'knR', 'anR', _dn, 'thighR');

    for (const nm of ['handL', 'handR', 'footL', 'footR']) {
      this.bone[nm].quaternion.slerp(_qa.identity(), 0.3);
      this.bone[nm].rotation.setFromQuaternion(this.bone[nm].quaternion);
    }
  }

  /* ---------------------------------------------------------------- */

  setVisible(v) {
    this.root.visible = v;
  }

  dispose() {
    this.root.removeFromParent();
    this.skeleton.dispose?.();
    this.flash.geometry.dispose();
  }
}

/** Exposed so Enemy can build hitboxes that track the animated bones. */
export const HITBOX_DEFS = [
  { bone: 'head', tag: 'head', damageScale: 3.0, offset: [0, 0.065, 0.005], half: [0.115, 0.125, 0.12] },
  { bone: 'chest', tag: 'chest', damageScale: 1.0, offset: [0, 0.14, 0], half: [0.20, 0.17, 0.15] },
  { bone: 'spine', tag: 'stomach', damageScale: 1.0, offset: [0, 0.03, 0], half: [0.175, 0.145, 0.14] },
  { bone: 'pelvis', tag: 'stomach', damageScale: 1.0, offset: [0, -0.04, 0], half: [0.17, 0.11, 0.14] },
  { bone: 'upperArmL', tag: 'arms', damageScale: 0.7, offset: [0, -0.13, 0], half: [0.075, 0.16, 0.085] },
  { bone: 'forearmL', tag: 'arms', damageScale: 0.7, offset: [0, -0.13, 0], half: [0.065, 0.15, 0.075] },
  { bone: 'upperArmR', tag: 'arms', damageScale: 0.7, offset: [0, -0.13, 0], half: [0.075, 0.16, 0.085] },
  { bone: 'forearmR', tag: 'arms', damageScale: 0.7, offset: [0, -0.13, 0], half: [0.065, 0.15, 0.075] },
  // Leg boxes are widened slightly inboard so a shot straight down the centreline
  // between the knees still connects — sub-centimetre gaps read as "my shots
  // don't register", not as realism.
  { bone: 'thighL', tag: 'legs', damageScale: 0.7, offset: [0.02, -0.22, 0], half: [0.12, 0.24, 0.12] },
  { bone: 'shinL', tag: 'legs', damageScale: 0.7, offset: [0.02, -0.21, 0], half: [0.105, 0.24, 0.11] },
  { bone: 'thighR', tag: 'legs', damageScale: 0.7, offset: [-0.02, -0.22, 0], half: [0.12, 0.24, 0.12] },
  { bone: 'shinR', tag: 'legs', damageScale: 0.7, offset: [-0.02, -0.21, 0], half: [0.105, 0.24, 0.11] },
];
